import { useState, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Button,
  Stack,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Avatar,
  useTheme,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { getBlogCategories, getBlogPosts } from 'src/utils/blog-utils';

export default function BlogView() {
  const theme = useTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [activeTab, setActiveTab] = useState(0);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isArticleOpen, setIsArticleOpen] = useState(false);
  const [email, setEmail] = useState('');

  // Email validation
  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // F&B Industry Categories
  const categories = getBlogCategories(theme);

  // F&B Industry News and Trends
  const blogPosts = useMemo(() => getBlogPosts(), []);

  // Filter and search functionality
  const filteredPosts = useMemo(() => {
    let filtered = blogPosts;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter((post) => post.category === activeCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      filtered = filtered.filter(
        (post) =>
          post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
          post.tags.some((tag) => tag.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    return filtered;
  }, [blogPosts, activeCategory, searchQuery]);

  const handleCategoryChange = (event, newValue) => {
    setActiveTab(newValue);
    const categoryId = categories[newValue].id;
    setActiveCategory(categoryId);
  };

  const handleArticleClick = (article) => {
    setSelectedArticle(article);
    setIsArticleOpen(true);
  };

  const handleCloseArticle = () => {
    setIsArticleOpen(false);
    setSelectedArticle(null);
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Add handleEmailChange function
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.primary.main,
            0.08
          )} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          pt: { xs: 10, md: 15 },
          pb: { xs: 8, md: 12 },
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: '2.5rem', md: '3.5rem' },
                fontWeight: 700,
                color: 'text.primary',
                mb: 3,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              F&B Industry News
              <Box component="span" sx={{ color: 'primary.main', display: 'block' }}>
                & Market Trends
              </Box>
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'text.secondary',
                mb: 4,
                lineHeight: 1.6,
                maxWidth: 800,
                mx: 'auto',
                fontSize: { xs: '1.125rem', md: '1.5rem' },
              }}
            >
              Stay informed with the latest breaking news, market trends, and industry insights
              affecting restaurants, suppliers, and the food & beverage industry. Your source for
              real-time updates.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Search and Filter Section */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            {/* Search Bar */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search news, trends, and industry updates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    '&:hover': {
                      bgcolor: 'background.neutral',
                    },
                  },
                }}
              />
            </Grid>

            {/* Category Tabs */}
            <Grid item xs={12} md={6}>
              <Tabs
                value={activeTab}
                onChange={handleCategoryChange}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  '& .MuiTab-root': {
                    minHeight: 48,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    px: 2,
                    borderRadius: 2,
                    '&.Mui-selected': {
                      color: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      borderColor: 'primary.main',
                    },
                  },
                  '& .MuiTabs-indicator': {
                    height: 0,
                  },
                }}
              >
                {categories.map((category, index) => (
                  <Tab
                    key={category.id}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {category.label}
                        <Chip
                          label={
                            blogPosts.filter((post) =>
                              category.id === 'all' ? true : post.category === category.id
                            ).length
                          }
                          size="small"
                          sx={{
                            bgcolor: alpha(category.color, 0.2),
                            color: category.color,
                            fontSize: '0.75rem',
                            height: 20,
                          }}
                        />
                      </Box>
                    }
                  />
                ))}
              </Tabs>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Featured Post */}
      {filteredPosts.length > 0 && filteredPosts[0]?.featured && (
        <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.neutral' }}>
          <Container maxWidth="lg">
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 4,
                textAlign: 'center',
              }}
            >
              Breaking News
            </Typography>
            <Card
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 3,
                boxShadow: theme.customShadows?.z16 || '0 8px 32px rgba(0,0,0,0.12)',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: theme.customShadows?.z24 || '0 16px 48px rgba(0,0,0,0.16)',
                },
                transition: 'all 0.3s ease-in-out',
                cursor: 'pointer',
              }}
              onClick={() => handleArticleClick(filteredPosts[0])}
            >
              <Grid container>
                <Grid item xs={12} md={6}>
                  <CardMedia
                    component="img"
                    image={filteredPosts[0].image}
                    alt={filteredPosts[0].title}
                    sx={{
                      height: { xs: 300, md: '100%' },
                      minHeight: 400,
                      objectFit: 'cover',
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <CardContent
                    sx={{ p: 4, height: '100%', display: 'flex', flexDirection: 'column' }}
                  >
                    <Box sx={{ mb: 2 }}>
                      <Chip
                        label={
                          filteredPosts[0].category.charAt(0).toUpperCase() +
                          filteredPosts[0].category.slice(1)
                        }
                        size="small"
                        sx={{
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                          color: 'primary.main',
                          fontWeight: 600,
                          mb: 2,
                        }}
                      />
                    </Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 700,
                        color: 'text.primary',
                        mb: 2,
                        fontSize: { xs: '1.5rem', md: '2rem' },
                      }}
                    >
                      {filteredPosts[0].title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: 'text.secondary',
                        lineHeight: 1.7,
                        mb: 3,
                        flex: 1,
                      }}
                    >
                      {filteredPosts[0].excerpt}
                    </Typography>

                    <Box sx={{ mt: 'auto' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Avatar
                          src={filteredPosts[0].authorAvatar}
                          alt={filteredPosts[0].author}
                          sx={{ width: 32, height: 32, mr: 2 }}
                        />
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 600, color: 'text.primary' }}
                          >
                            {filteredPosts[0].author}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {formatDate(filteredPosts[0].publishDate)} • {filteredPosts[0].readTime}
                          </Typography>
                        </Box>
                      </Box>

                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        {filteredPosts[0].tags.slice(0, 3).map((tag, index) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{
                              fontSize: '0.75rem',
                              borderColor: alpha(theme.palette.primary.main, 0.3),
                              color: 'primary.main',
                            }}
                          />
                        ))}
                      </Stack>

                      <Button
                        variant="contained"
                        size="large"
                        sx={{
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleArticleClick(filteredPosts[0]);
                        }}
                      >
                        Read Full Article
                      </Button>
                    </Box>
                  </CardContent>
                </Grid>
              </Grid>
            </Card>
          </Container>
        </Box>
      )}

      {/* Blog Posts Grid */}
      <Box sx={{ py: { xs: 4, md: 6 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          {filteredPosts.length > 0 ? (
            <>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 4,
                  textAlign: 'center',
                }}
              >
                {activeCategory === 'all'
                  ? 'Latest News & Trends'
                  : `${categories.find((c) => c.id === activeCategory)?.label}`}
                {searchQuery && ` - Search Results for "${searchQuery}"`}
              </Typography>

              <Grid container spacing={4}>
                {filteredPosts.map((post, index) => (
                  <Grid item xs={12} sm={6} lg={4} key={post.id}>
                    <Card
                      sx={{
                        height: '100%',
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: theme.customShadows?.z8 || '0 4px 16px rgba(0,0,0,0.08)',
                        cursor: 'pointer',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: theme.customShadows?.z16 || '0 8px 32px rgba(0,0,0,0.12)',
                        },
                        transition: 'all 0.3s ease-in-out',
                      }}
                      onClick={() => handleArticleClick(post)}
                    >
                      <CardMedia
                        component="img"
                        image={post.image}
                        alt={post.title}
                        sx={{
                          height: 200,
                          objectFit: 'cover',
                        }}
                      />
                      <CardContent
                        sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            label={post.category.charAt(0).toUpperCase() + post.category.slice(1)}
                            size="small"
                            sx={{
                              bgcolor: alpha(theme.palette.primary.main, 0.1),
                              color: 'primary.main',
                              fontWeight: 600,
                              mb: 2,
                            }}
                          />
                        </Box>

                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 2,
                            lineHeight: 1.4,
                            flex: 1,
                          }}
                        >
                          {post.title}
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            lineHeight: 1.6,
                            mb: 3,
                            flex: 1,
                          }}
                        >
                          {post.excerpt}
                        </Typography>

                        <Box sx={{ mt: 'auto' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar
                              src={post.authorAvatar}
                              alt={post.author}
                              sx={{ width: 28, height: 28, mr: 2 }}
                            />
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {post.author}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                {formatDate(post.publishDate)} • {post.readTime}
                              </Typography>
                            </Box>
                          </Box>

                          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                            {post.tags.slice(0, 2).map((tag, tagIndex) => (
                              <Chip
                                key={tagIndex}
                                label={tag}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: '0.7rem',
                                  borderColor: alpha(theme.palette.primary.main, 0.3),
                                  color: 'primary.main',
                                  height: 20,
                                }}
                              />
                            ))}
                          </Stack>

                          <Button
                            variant="outlined"
                            size="small"
                            fullWidth
                            sx={{
                              borderRadius: 2,
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': {
                                borderColor: 'primary.dark',
                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                              },
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArticleClick(post);
                            }}
                          >
                            Read More
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Iconify
                icon="eva:search-outline"
                sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }}
              />
              <Typography variant="h5" sx={{ color: 'text.secondary', mb: 1 }}>
                No articles found
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Try adjusting your search terms or category filter
              </Typography>
              <Button
                variant="outlined"
                onClick={() => {
                  setSearchQuery('');
                  setActiveCategory('all');
                  setActiveTab(0);
                }}
              >
                Clear Filters
              </Button>
            </Box>
          )}
        </Container>
      </Box>

      {/* Newsletter Section */}
      <Box sx={{ py: { xs: 6, md: 8 }, bgcolor: 'background.neutral' }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: 'text.primary',
                mb: 2,
              }}
            >
              Stay Updated with Industry News
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: 'text.secondary',
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Get the latest tips, trends, and success stories delivered to your inbox
            </Typography>

            <Box sx={{ maxWidth: 500, mx: 'auto' }}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                sx={{ alignItems: 'center' }}
              >
                <TextField
                  fullWidth
                  placeholder="Enter your email"
                  value={email}
                  onChange={handleEmailChange}
                  size="large"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: 'background.paper',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  size="large"
                  disabled={!isValidEmail}
                  sx={{
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    fontWeight: 600,
                    minWidth: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Subscribe
                </Button>
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', mt: 2, display: 'block' }}
              >
                No spam, unsubscribe at any time
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Article Dialog */}
      <Dialog
        open={isArticleOpen}
        onClose={handleCloseArticle}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            maxHeight: '90vh',
          },
        }}
      >
        {selectedArticle && (
          <>
            <DialogTitle sx={{ pb: 1 }}>
              <Box
                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
              >
                <Box sx={{ flex: 1 }}>
                  <Chip
                    label={
                      selectedArticle.category.charAt(0).toUpperCase() +
                      selectedArticle.category.slice(1)
                    }
                    size="small"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      color: 'primary.main',
                      fontWeight: 600,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      color: 'text.primary',
                      mb: 2,
                      fontSize: {
                        xs: '1rem',
                        sm: '2rem',
                      },
                    }}
                  >
                    {selectedArticle.title}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Avatar
                      src={selectedArticle.authorAvatar}
                      alt={selectedArticle.author}
                      sx={{ width: 32, height: 32, mr: 2 }}
                    />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          fontSize: { xs: '0.7rem', sm: '1rem' },
                        }}
                      >
                        {selectedArticle.author}
                      </Typography>
                      <Typography
                        sx={{ color: 'text.secondary', fontSize: { xs: '0.7rem', sm: '1rem' } }}
                      >
                        {formatDate(selectedArticle.publishDate)} • {selectedArticle.readTime}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <IconButton onClick={handleCloseArticle} sx={{ ml: 2 }}>
                  <Iconify icon="eva:close-fill" />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 0 }}>
              <Box sx={{ mb: 3 }}>
                <img
                  src={selectedArticle.image}
                  alt={selectedArticle.title}
                  style={{
                    width: '100%',
                    height: 'auto',
                    borderRadius: '12px',
                    marginBottom: '24px',
                  }}
                />
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.secondary',
                    lineHeight: 1.8,
                    fontSize: { xs: '0.8rem', sm: '1.1rem' },
                    whiteSpace: 'pre-line',
                  }}
                >
                  {selectedArticle.content}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Stack
                direction="row"
                spacing={1}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  mr: 1,
                  msOverflowStyle: 'none',
                  scrollbarWidth: 'none',
                  '&::-webkit-scrollbar': {
                    display: 'none',
                  },
                }}
              >
                {selectedArticle.tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{
                      fontSize: '0.75rem',
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      color: 'primary.main',
                    }}
                  />
                ))}
              </Stack>
              <Button variant="outlined" onClick={handleCloseArticle}>
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
